import React, {useState} from 'react';
import './App.css';
import useWebSocket from 'react-use-websocket';
import PopUp from './components/PopUp'
import PopUpYesNo from "./components/PopUpYesNo";
import PopUpAddCustomer from "./components/PopUpAddCustomer";
import {DataGrid} from '@mui/x-data-grid';

const WS_URL = 'wss://releases.snipercam.pl/customers/ws';

const dataGridStyles = {
    maxWidth: 1200,
    margin: '20px auto',
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#a70000',
        color: 'white',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#a70000',
        color: 'white',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 'bold',
    },
    '& .MuiDataGrid-sortIcon': {
        color: 'white',
        opacity: 1,
    },
    '& .MuiDataGrid-menuIconButton': {
        color: 'white',
    },
    '& .MuiDataGrid-iconButtonContainer': {
        visibility: 'visible',
        '& .MuiIconButton-root': {
            color: 'white',
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
        },
    },
};

function CustomerList({token, onLogout}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [error, setError] = useState(null);
    const [websocketConnected, setWebsocketConnected] = useState(false);
    const [removeSelectedCustomer, setRemoveSelectedCustomer] = useState(false);
    const [showAddCustomerPopup, setShowAddCustomerPopup] = useState(false);

    const getAuthHeaders = () => {
        const requestHeaders = new Headers();
        requestHeaders.set("Authorization", "Bearer " + token)
        return requestHeaders
    }

    const updateCustomerList = () => {
        const requestHeaders = getAuthHeaders();
        fetch('api/customers/', {
            headers: requestHeaders
        })
            .then(response => {
                if (response.status === 200) {
                    return response.json()
                }
                if (response.status === 401) {
                    onLogout()
                }
                setError("Failed getting customer list")
                return []
            })
            .then(data => {
                setData(data);
                if (selectedCustomer) {
                    const foundCustomer = data.find((customer) => customer.name === selectedCustomer.name);
                    setSelectedCustomer(foundCustomer)
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                setLoading(false);
                onLogout()
            });
    }

    useWebSocket(WS_URL + "?token=" + token, {
        onOpen: () => {
            setWebsocketConnected(true)
            console.log('WebSocket connection established.');
            updateCustomerList()
        },
        onMessage: (message) => {
            if (message.data === "pong") {
                return
            }
            console.log('WS message received: ' + message.data);
            updateCustomerList();
        },
        onClose: () => {
            setWebsocketConnected(false)
        },
        onError: (err) => {
            setWebsocketConnected(false)
            updateCustomerList()
        },
        heartbeat: {
            interval: 1000,
            timeout: 5000
        },
        reconnectInterval: 5000,
        shouldReconnect: () => {
            return true
        }
    });


    if (loading) {
        return <div><h1>Loading...</h1></div>;
    }

    const handleCustomerClick = (customer) => {
        setError("")
        setSelectedCustomer(customer);
    };

    const handleBackClick = () => {
        setError("")
        setSelectedCustomer(null);
    };

    const handleError = (response, error_message) => {
        if (response.status === 401) {
            onLogout()();
        } else {
            setError(error_message + " failed with code: " + response.status);
        }
    }

    const handleRemoveDeviceClick = async (customer_name, device) => {
        setError("")
        const requestHeaders = getAuthHeaders();
        requestHeaders.set("Content-Type", "application/json")
        const response = await fetch('api/customers/authorise_device', {
            method: 'DELETE',
            headers: requestHeaders,
            body: JSON.stringify({name: customer_name, system_id: device.system_id})
        });
        if (response.status !== 200) {
            handleError(response, "Removing device " + device);
        }
    };

    const handleRemoveCustomerClick = async (customer) => {
        setError("")
        let approved_devices_count = selectedCustomer.devices.length;
        if (approved_devices_count > 0) {
            if (approved_devices_count === 1) {
                setError("Customer has " + selectedCustomer.devices.length + " device, remove it first!");
            } else {
                setError("Customer has " + selectedCustomer.devices.length + " devices, remove them first!");
            }
            return
        }
        const requestHeaders = getAuthHeaders();
        requestHeaders.set("Content-Type", "application/json")
        const response = await fetch('api/customers/', {
            method: 'DELETE',
            headers: requestHeaders,
            body: JSON.stringify({name: customer.name})
        });
        if (response.status !== 200) {
            handleError(response, "Removing customer " + customer.name);
        }
    };

    const approveDevice = async (customer_name, device) => {
        setError("")
        const requestHeaders = getAuthHeaders();
        requestHeaders.set("Content-Type", "application/json")
        const response = await fetch('api/customers/authorise_device', {
            method: 'POST',
            headers: requestHeaders,
            body: JSON.stringify({name: customer_name, device_name: device.device_name, system_id: device.system_id})
        });
        if (response.status !== 200) {
            handleError(response, "Approving device " + device.device_name);
        }
    };

    const addCustomer = async (customerName) => {
        setError("")
        if (customerName == null || customerName === "") {
            setError("Customer name cannot be empty");
            return false;
        }
        let fake_token = (Math.random() + 1).toString(36).substring(2);
        const requestHeaders = getAuthHeaders();
        requestHeaders.set("Content-Type", "application/json")
        const response = await fetch('api/customers/', {
            method: 'POST',
            headers: requestHeaders,
            body: JSON.stringify({name: customerName, token: "FakeToken" + fake_token + "TokenFake"})
        });
        if (response.status !== 200) {
            handleError(response, "Adding customer " + customerName);
            return false;
        }
        setShowAddCustomerPopup(false);
        return true;
    };

    const compareVersions = (v1, v2) => {
        const parts1 = (v1 || '').replace(/^v/, '').split('.').map(Number);
        const parts2 = (v2 || '').replace(/^v/, '').split('.').map(Number);
        for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
            const p1 = parts1[i] || 0;
            const p2 = parts2[i] || 0;
            if (p1 > p2) return 1;
            if (p1 < p2) return -1;
        }
        return 0;
    };

    return (
        <div className="CustomerList">
            {selectedCustomer ? (
                <div>
                    <h1>{selectedCustomer.name} devices</h1>
                    <button className="DeleteButton" disabled={!websocketConnected}
                            onClick={() => setRemoveSelectedCustomer(true)}>Remove Customer
                    </button>
                    <button onClick={handleBackClick}>Back</button>

                    <DataGrid
                        rows={[
                            ...selectedCustomer.devices.map((device, idx) => ({
                                ...device,
                                customer_name: selectedCustomer.name,
                                is_authorised: true,
                                unique_id: `authorised-${device.system_id}-${idx}`
                            })),
                            ...selectedCustomer.unauthorised_devices.map((device, idx) => ({
                                ...device,
                                customer_name: selectedCustomer.name,
                                is_authorised: false,
                                unique_id: `unauthorised-${device.system_id}-${idx}`
                            }))
                        ]}
                        columns={[
                            {field: 'customer_name', headerName: 'Customer Name', flex: 1},
                            {field: 'system_id', headerName: 'Device System ID', flex: 1},
                            {field: 'device_name', headerName: 'Device Name', flex: 1},
                            {
                                field: 'latest_snipercam_release_version',
                                headerName: 'Release',
                                flex: 1,
                                sortComparator: compareVersions
                            },
                            {
                                field: 'actions',
                                headerName: '',
                                flex: 1,
                                sortable: false,
                                renderCell: (params) => (
                                    params.row.is_authorised ? (
                                        <button className="DeleteButton" disabled={!websocketConnected}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveDeviceClick(selectedCustomer.name, params.row);
                                                }}>Remove
                                        </button>
                                    ) : (
                                        <button className="AddButton" disabled={!websocketConnected}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    approveDevice(selectedCustomer.name, params.row);
                                                }}>Approve device
                                        </button>
                                    )
                                )
                            }
                        ]}
                        getRowId={(row) => row.unique_id}
                        autoHeight
                        disableRowSelectionOnClick
                        sx={dataGridStyles}
                    />
                    <h3>{error}</h3>

                    {(websocketConnected && removeSelectedCustomer) ? <PopUpYesNo
                        text={"Are you sure you want to remove the customer " + selectedCustomer.name + "?"}
                        callback_function={(agreed) => {
                            setRemoveSelectedCustomer(false)
                            if (agreed) {
                                handleRemoveCustomerClick(selectedCustomer)
                            }
                        }}/> : ""}
                </div>
            ) : (
                <div>
                    <h1>Customers</h1>
                    <button className="AddButton" disabled={!websocketConnected}
                            onClick={() => setShowAddCustomerPopup(true)}>Add Customer
                    </button>
                    <DataGrid
                        rows={[...data.flatMap(customer =>
                            customer.devices.length > 0
                                ? customer.devices.map((device, idx) => ({
                                    ...device,
                                    customer_name: customer.name,
                                    id: `${customer.name}-${device.system_id}-${idx}`
                                }))
                                : [{
                                    customer_name: customer.name,
                                    system_id: '',
                                    device_name: '',
                                    device_id: '',
                                    latest_snipercam_release_version: '',
                                    id: `${customer.name}-empty`
                                }]
                        )]}
                        columns={[
                            {field: 'customer_name', headerName: 'Customer Name', flex: 1},
                            {field: 'system_id', headerName: 'Device System ID', flex: 1},
                            {field: 'device_name', headerName: 'Device Name', flex: 1},
                            {
                                field: 'latest_snipercam_release_version',
                                headerName: 'Release',
                                flex: 1,
                                sortComparator: compareVersions
                            }
                        ]}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    {field: 'latest_snipercam_release_version', sort: 'desc'},
                                    {field: 'customer_name', sort: 'asc'}
                                ]
                            }
                        }}
                        onRowClick={(params) => handleCustomerClick(data.find(c => c.name === params.row.customer_name))}
                        autoHeight
                        disableRowSelectionOnClick
                        sx={{
                            ...dataGridStyles,
                            cursor: 'pointer',
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                    />
                    <h3>{error}</h3>
                    {(websocketConnected && showAddCustomerPopup) && <PopUpAddCustomer
                        onAdd={addCustomer}
                        onClose={() => {
                            setShowAddCustomerPopup(false);
                            setError("");
                        }}
                        error={error}
                    />}
                </div>
            )}
            {(!loading && !websocketConnected) ? <PopUp text={"Server connection lost!"}/> : ""}
        </div>
    );
}

export default CustomerList;
